import React, { useState, useEffect, useMemo } from "react";
import {
  Search, Filter, X, AlertTriangle,
  Users, Ban, Clock, Settings,
  FileText, Calendar, DollarSign, ShoppingBag,
  Phone, Mail, MapPin, User, Star, MessageSquare,
  Download, Trash2, Edit, Eye
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import AdminHeader from "../../Headers/AdminHeader";
import SharedFooter3 from "../../Footer/SharedFooter3";
import { useSelector } from "react-redux";
import { getAllJobs, useAppDispatch, updateUserJob, deleteUserJob } from "../../redux";
import { GlobalService } from "../../modules";
import EditJobModal from "./EditJobModal";
import { useNavigate } from "react-router-dom";

const AllOrdersPage = () => {
  // Redux hooks
  const dispatch = useAppDispatch();
  const { allJobs } = useSelector((state: any) => state.auth);
  const isLoading = useSelector((state: any) => state.loader.isLoading);

  // State management
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [orderType, setOrderType] = useState("all");
  const [status, setStatus] = useState("all");
  const [showOrderDetails, setShowOrderDetails] = useState(null);
  const [sortBy, setSortBy] = useState("newest");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(10);
  const [filters, setFilters] = useState({
    paymentStatus: "all",
    serviceType: "all",
    dateRange: "all"
  });

  // State for Edit Job Modal
  const [editJobModalOpen, setEditJobModalOpen] = useState(false);
  const [editingJob, setEditingJob] = useState(null);

  // Fetch orders on component mount and when filters change
  useEffect(() => {
    // fetchOrders();
  }, [currentPage, orderType, status, sortBy, filters]);

  const fetchOrders = async () => {
    try {
      const filterParams: any = {};

      // Map status dropdown to backend status IDs
      if (status !== "all") {
        switch (status) {
          case "pending":
            filterParams.status = [1];
            break;
          case "in_progress":
            filterParams.status = [2];
            break;
          case "completed":
            filterParams.status = [3];
            break;
          case "cancelled":
            filterParams.status = [4];
            break;
          case "inactive":
            filterParams.status = [5];
            break;
          case "active":
            filterParams.status = [6];
            break;
          case "approved":
            filterParams.status = [7];
            break;
          case "flagged":
            filterParams.isFlagged = true; // Use isFlagged property instead of status
            break;
        }
      }

      // Map order type to emergency filter
      if (orderType !== "all") {
        filterParams.isEmergency = orderType === "emergency";
      }

      // Map service type to category filter
      if (filters.serviceType !== "all") {
        filterParams.category = filters.serviceType;
      }

      // Map payment status
      if (filters.paymentStatus !== "all") {
        filterParams.paymentStatus = filters.paymentStatus;
      }

      // Map date range
      if (filters.dateRange !== "all") {
        filterParams.dateRange = filters.dateRange;
      }

      // Add search query as keyword filter if 3+ characters
      if (searchQuery && searchQuery.length >= 3) {
        filterParams.keyword = searchQuery;
      }

      // Add sorting according to backend format
      const sortOptions = [];

      switch (sortBy) {
        case "newest":
          sortOptions.push({ orderBy: "createdAt", order: "DESC" });
          break;
        case "oldest":
          sortOptions.push({ orderBy: "createdAt", order: "ASC" });
          break;
        case "amount_high":
          sortOptions.push({ orderBy: "budget", order: "DESC" });
          break;
        case "amount_low":
          sortOptions.push({ orderBy: "budget", order: "ASC" });
          break;
        default:
          sortOptions.push({ orderBy: "createdAt", order: "DESC" });
      }

      await dispatch(getAllJobs({
        page: currentPage,
        limit,
        sort: sortOptions,
        filters: filterParams
      }));
    } catch (error) {
      console.error("Failed to fetch orders:", error);
    }
  };

  // Transform jobs data to orders format
  const transformJobsToOrders = (jobs) => {
    if (!jobs || !Array.isArray(jobs)) return [];

    return jobs.map(job => {
      // Map backend status to display status
      let displayStatus = "pending";
      const jobStatus = Number(job.status?.id);

      switch (jobStatus) {
        case 1:
          displayStatus = "pending";
          break;
        case 2:
          displayStatus = "in_progress";
          break;
        case 3:
          displayStatus = "completed";
          break;
        case 4:
          displayStatus = "cancelled";
          break;
        case 5:
          displayStatus = "inactive";
          break;
        case 6:
          displayStatus = "active";
          break;
        case 7:
          displayStatus = "approved";
          break;
        case 8:
          displayStatus = "flagged";
          break;
        case 9:
          displayStatus = "deleted";
          break;
        case 10:
          displayStatus = "contracted";
          break;
        default:
          displayStatus = "pending";
      }

      // Check if order is flagged (separate from status)
      const isFlagged = job.isFlagged || false;

      return {
        id: job.id,
        type: job.isEmergency ? "emergency" : "standard",
        service: job.categoryId?.name || "General Service",
        status: displayStatus,
        origionalJob: job,
        isLocal: job.isLocal,
        customer: {
          name: job.contactName || "Anonymous",
          email: job.contactEmail || "N/A",
          phone: job.contactPhone || "N/A",
          address: `${job.address}, ${job.city}, ${job.province}`
        },
        provider: job.provider ? {
          name: job.provider.name || "Unassigned",
          company: job.provider.company || "N/A",
          rating: job.provider.rating || 0,
          verified: job.provider.verified || false
        } : null,
        orderDate: job.createdAt,
        scheduledDate: job.scheduledDate || null,
        amount: job.budget || 0,
        paymentStatus: job.paymentStatus || "pending",
        priority: job.isEmergency ? "high" : "normal",
        description: job.description || job.title,
        isFlagged: isFlagged,
        notes: [
          {
            time: job.createdAt,
            author: "System",
            text: "Order received"
          }
        ]
      };
    });
  };

  // Get transformed orders from real data
  const orders = useMemo(() => {
    return transformJobsToOrders(allJobs.data);
  }, [allJobs.data]);

  // Calculate pagination
  const totalPages = allJobs.totalPages || 1;
  const totalCount = allJobs.totalCount || 0;

  // Handle search with debounce
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Debounced search effect - trigger after 3 characters
  useEffect(() => {
    if (searchQuery.length >= 3 || searchQuery.length === 0) {
      const timeoutId = setTimeout(() => {
        fetchOrders();
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [searchQuery]);

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({ ...prev, [filterType]: value }));
    setCurrentPage(1); // Reset to first page when filtering
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Handle flag issue
  const handleFlagIssue = async (order) => {
    try {
      const job = allJobs.data.find(job => job.id === order.id);
      
      // Add safety check for job existence
      if (!job) {
        GlobalService.showToast("error", "Error", "Job not found!");
        return;
      }

      const updateData = {
        ...job,
        status: job.status?.id ? { id: Number(job.status.id) } : null,
        emergencyDays: job.emergencyDays?.id ? { id: Number(job.emergencyDays.id) } : null,
        emergencyTimeSlot: job.emergencyTimeSlot?.id ? { id: Number(job.emergencyTimeSlot.id) } : null,
        budgetType: job.budgetType?.id ? { id: Number(job.budgetType.id) } : null,
        deadlineType: job.deadlineType?.id ? { id: Number(job.deadlineType.id) } : null,
        serviceType: job.serviceType?.id ? { id: Number(job.serviceType.id) } : null,
        equipmentType: job.equipmentType?.id ? { id: Number(job.equipmentType.id) } : null,
        propertyType: job.propertyType?.id ? { id: Number(job.propertyType.id) } : null,
        isFlagged: true // Set flagged property instead of changing status
      };

      const result = await dispatch(updateUserJob({ id: order.id, data: updateData }));

      if (updateUserJob.fulfilled.match(result)) {
        GlobalService.showToast("success", "Success", "Order flagged successfully!");
        // Refresh the orders list
        fetchOrders();
        // Close the modal
        setShowOrderDetails(null);
      } else {
        GlobalService.showToast("error", "Error", "Failed to flag order. Please try again.");
      }
    } catch (error) {
      console.error("Error flagging order:", error);
      GlobalService.showToast("error", "Error", "Failed to flag order. Please try again.");
    }
  };

  // Handle deactivate/activate job
  const handleToggleJobStatus = async (orderId, currentStatus) => {
    try {
      let newStatusId;
      let successMessage;

      if (currentStatus === "inactive") {
        // If currently inactive, make it approved
        newStatusId = 7; // Approved status
        successMessage = "Order activated successfully!";
      } else {
        // If currently active/approved, make it inactive
        newStatusId = 5; // Inactive status
        successMessage = "Order deactivated successfully!";
      }

      const updateData = {
        status: { id: newStatusId }
      };

      const result = await dispatch(updateUserJob({ id: orderId, data: updateData }));

      if (updateUserJob.fulfilled.match(result)) {
        GlobalService.showToast("success", "Success", successMessage);
        // Refresh the orders list
        fetchOrders();
        // Close the modal
        setShowOrderDetails(null);
      } else {
        GlobalService.showToast("error", "Error", "Failed to update order status. Please try again.");
      }
    } catch (error) {
      console.error("Error updating order status:", error);
      GlobalService.showToast("error", "Error", "Failed to update order status. Please try again.");
    }
  };

  // Handle permanent deletion
  const handleDeleteJob = async (orderId) => {
    // Show confirmation dialog
    if (!window.confirm("Are you sure you want to permanently delete this order? This action cannot be undone.")) {
      return;
    }

    try {
      const result = await dispatch(deleteUserJob({ id: orderId }));

      if (deleteUserJob.fulfilled.match(result)) {
        GlobalService.showToast("success", "Success", "Order deleted successfully!");
        // Refresh the orders list
        fetchOrders();
        // Close the modal
        setShowOrderDetails(null);
      } else {
        GlobalService.showToast("error", "Error", "Failed to delete order. Please try again.");
      }
    } catch (error) {
      console.error("Error deleting order:", error);
      GlobalService.showToast("error", "Error", "Failed to delete order. Please try again.");
    }
  };
  const navigate = useNavigate();
  // Handle edit job
  const handleEditJob = (job) => {

    setEditingJob(job);
    setEditJobModalOpen(true);
  };

  // Handle edit job success
  const handleEditJobSuccess = () => {
    fetchOrders();
    setEditJobModalOpen(false);
    setEditingJob(null);
  };

  // Handle bulk actions
  const handleBulkAction = (action) => {
    console.log(`Bulk ${action} for orders:`, selectedOrders);
    // Implement bulk actions here
  };

  // Calculate stats
  const stats = useMemo(() => {
    const totalOrders = totalCount; // Use total count from API
    const pendingOrders = orders.filter(order => order.status === "pending").length;
    const flaggedOrders = orders.filter(order => order.isFlagged).length;
    const totalRevenue = orders.reduce((sum, order) => sum + order.amount, 0);
    const activeProviders = new Set(orders.filter(order => order.provider).map(order => order.provider.name)).size;

    return {
      totalOrders,
      pendingOrders,
      flaggedOrders,
      totalRevenue,
      activeProviders
    };
  }, [orders, totalCount]);

  // Order Details Modal Component
  const OrderDetailsModal = ({ order, isOpen, onClose }) => {
    if (!order || !isOpen) return null;

    const getStatusBadge = (status) => {
      const styles = {
        pending: "bg-yellow-100 text-yellow-700",
        in_progress: "bg-blue-100 text-blue-700",
        completed: "bg-green-100 text-green-700",
        cancelled: "bg-red-100 text-red-700",
        inactive: "bg-gray-100 text-gray-700",
        active: "bg-green-100 text-green-700",
        approved: "bg-green-100 text-green-700"
      };
      return styles[status] || "bg-gray-100 text-gray-700";
    };

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" style={{ textAlign: "left" }}>
        <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b sticky top-0 bg-white">
            <div className="flex justify-between items-start">
              <div>
                <div className="flex items-center gap-3">
                  <Badge variant={undefined} className={getStatusBadge(order.status)}>
                    {order.status.replace("_", " ").toUpperCase()}
                  </Badge>
                  {order.type === "emergency" && (
                    <Badge variant="outline" className="border-red-200 text-red-700">
                      EMERGENCY
                    </Badge>
                  )}
                  {order.isFlagged && (
                    <Badge variant="outline" className="border-orange-200 text-orange-700">
                      FLAGGED
                    </Badge>
                  )}
                </div>
                <h2 className="text-xl font-semibold mt-2">Order {order.id}</h2>
                <p className="text-sm text-gray-500">{order.service}</p>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Customer Information */}
            <div className="space-y-4">
              <h3 className="font-medium">Customer Information</h3>
              <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm text-gray-500">Name</span>
                    <p className="font-medium">{order.customer.name}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Phone</span>
                    <p className="font-medium">{order.customer.phone}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Email</span>
                    <p className="font-medium">{order.customer.email}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Address</span>
                    <p className="font-medium">{order.customer.address}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Provider Information */}
            {order.provider && (
              <div className="space-y-4">
                <h3 className="font-medium">Service Provider</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <User className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="font-medium">{order.provider.name}</h4>
                        <p className="text-sm text-gray-500">{order.provider.company}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Star className="h-4 w-4 text-yellow-400 fill-current" />
                      <span>{order.provider.rating}</span>
                      {order.provider.verified && (
                        <Badge variant="secondary" className="bg-green-100 text-green-700">
                          Verified
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Order Details */}
            <div className="space-y-4">
              <h3 className="font-medium">Order Details</h3>
              <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm text-gray-500">Order Date</span>
                    <p className="font-medium">
                      {new Date(order.orderDate).toLocaleString()}
                    </p>
                  </div>
                  {order.scheduledDate && (
                    <div>
                      <span className="text-sm text-gray-500">Scheduled Date</span>
                      <p className="font-medium">
                        {new Date(order.scheduledDate).toLocaleString()}
                      </p>
                    </div>
                  )}
                  <div>
                    <span className="text-sm text-gray-500">Amount</span>
                    <p className="font-medium">${order.amount.toFixed(2)}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Payment Status</span>
                    <Badge variant={undefined} className={
                      order.paymentStatus === "paid"
                        ? "bg-green-100 text-green-700"
                        : "bg-yellow-100 text-yellow-700"
                    }>
                      {order.paymentStatus.toUpperCase()}
                    </Badge>
                  </div>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Description</span>
                  <p className="mt-1">{order.description}</p>
                </div>
              </div>
            </div>

            {/* Activity Log */}
            <div className="space-y-4">
              <h3 className="font-medium">Activity Log</h3>
              <div className="space-y-4">
                {order.notes.map((note, index) => (
                  <div key={index} className="flex gap-3">
                    <div className={`p-2 rounded-lg ${note.author === "System" ? "bg-gray-100" :
                      note.author === "Provider" ? "bg-blue-100" :
                        "bg-green-100"
                      }`}>
                      {note.author === "System" ? (
                        <Settings className="h-4 w-4 text-gray-600" />
                      ) : (
                        <User className="h-4 w-4 text-blue-600" />
                      )}
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">
                        {new Date(note.time).toLocaleString()}
                      </div>
                      <p className="mt-1">{note.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Admin Actions */}
            <div className="space-y-4 pt-4 border-t">
              <h3 className="font-medium">Admin Actions</h3>
              <div className="flex flex-wrap gap-3">
                <Button 
                  variant="outline" 
                  className="flex items-center gap-2"
                  onClick={() => {
                    if (order.customer.phone && order.customer.phone !== "N/A") {
                      window.open(`tel:${order.customer.phone}`, "_self");
                    } else {
                      GlobalService.showToast("error", "Error", "No phone number available for this customer");
                    }
                  }}
                >
                  <MessageSquare className="h-4 w-4" />
                  Contact Customer
                </Button>
                <Button
                  variant="outline"
                  className="flex items-center gap-2"
                  onClick={() => handleEditJob(order)}
                >
                  <Edit className="h-4 w-4" />
                  Edit Order
                </Button>
                <Button
                  variant="outline"
                  className="flex items-center gap-2 text-yellow-600"
                  onClick={() => handleFlagIssue(order)}
                >
                  <AlertTriangle className="h-4 w-4" />
                  Flag Issue
                </Button>
                <Button
                  variant="outline"
                  className={`flex items-center gap-2 ${order.status === "inactive" ? "text-green-600" : "text-red-600"
                    }`}
                  onClick={() => handleToggleJobStatus(order.id, order.status)}
                >
                  <Ban className="h-4 w-4" />
                  {order.status === "inactive" ? "Activate Order" : "Deactivate Order"}
                </Button>
                <Button
                  variant="outline"
                  className="flex items-center gap-2 text-red-600 border-red-600 hover:bg-red-50"
                  onClick={() => handleDeleteJob(order.id)}
                >
                  <Trash2 className="h-4 w-4" />
                  Permanently Delete
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" style={{ textAlign: "left" }}>
      <AdminHeader />

      <main className="flex-1 lg:pl-64 pt-16">
        <div className="p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">All Orders</h1>
                <p className="text-gray-600 mt-1">Manage and monitor all service orders</p>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" className="flex items-center gap-2">
                  <Download className="h-4 w-4" />
                  Export Orders
                </Button>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Total Orders</p>
                    <p className="text-2xl font-semibold">{stats.totalOrders.toLocaleString()}</p>
                    <p className="text-xs text-gray-400">All time orders</p>
                  </div>
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <ShoppingBag className="h-5 w-5 text-blue-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Pending Orders</p>
                    <p className="text-2xl font-semibold">{stats.pendingOrders.toLocaleString()}</p>
                    <p className="text-xs text-gray-400">Awaiting action</p>
                  </div>
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <Clock className="h-5 w-5 text-yellow-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Flagged Orders</p>
                    <p className="text-2xl font-semibold">{stats.flaggedOrders.toLocaleString()}</p>
                    <p className="text-xs text-gray-400">Require attention</p>
                  </div>
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <AlertTriangle className="h-5 w-5 text-orange-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Total Revenue</p>
                    <p className="text-2xl font-semibold">${stats.totalRevenue.toLocaleString()}</p>
                    <p className="text-xs text-gray-400">From all orders</p>
                  </div>
                  <div className="p-2 bg-green-100 rounded-lg">
                    <DollarSign className="h-5 w-5 text-green-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Active Providers</p>
                    <p className="text-2xl font-semibold">{stats.activeProviders.toLocaleString()}</p>
                    <p className="text-xs text-gray-400">Currently working</p>
                  </div>
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Users className="h-5 w-5 text-purple-600" />
                  </div>
                </div>
              </Card>
            </div>

            {/* Search and Filters */}
            <Card className="p-4">
              <div className="flex flex-col lg:flex-row justify-between gap-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full lg:w-auto">
                  <div className="relative flex-1 w-full sm:w-[300px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search orders..."
                      value={searchQuery}
                      onChange={handleSearchChange}
                      className="w-full pl-10 pr-4 py-2 border rounded-lg"
                    />
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setShowFilters(!showFilters)}
                    className="flex items-center gap-2 w-full sm:w-auto justify-center"
                  >
                    <Filter className="h-5 w-5" />
                    Filters
                  </Button>
                </div>

                <div className="flex flex-col sm:flex-row items-stretch gap-4 w-full lg:w-auto">
                  <select
                    value={orderType}
                    onChange={(e) => setOrderType(e.target.value)}
                    className="px-4 py-2 border rounded-lg w-full sm:w-auto"
                  >
                    <option value="all">All Types</option>
                    <option value="standard">Standard</option>
                    <option value="emergency">Emergency</option>
                  </select>

                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="px-4 py-2 border rounded-lg w-full sm:w-auto"
                  >
                    <option value="all">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="inactive">Inactive</option>
                    <option value="active">Active</option>
                    <option value="approved">Approved</option>
                    <option value="flagged">Flagged</option>
                  </select>

                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="px-4 py-2 border rounded-lg w-full sm:w-auto"
                  >
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                    <option value="amount_high">Highest Amount</option>
                    <option value="amount_low">Lowest Amount</option>
                  </select>
                </div>
              </div>

              {/* Advanced Filters */}
              {showFilters && (
                <div className="mt-4 pt-4 border-t">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Payment Status
                      </label>
                      <select
                        value={filters.paymentStatus}
                        onChange={(e) => handleFilterChange("paymentStatus", e.target.value)}
                        className="w-full px-4 py-2 border rounded-lg"
                      >
                        <option value="all">All</option>
                        <option value="paid">Paid</option>
                        <option value="pending">Pending</option>
                        <option value="failed">Failed</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Service Type
                      </label>
                      <select
                        value={filters.serviceType}
                        onChange={(e) => handleFilterChange("serviceType", e.target.value)}
                        className="w-full px-4 py-2 border rounded-lg"
                      >
                        <option value="all">All Services</option>
                        <option value="plumbing">Plumbing</option>
                        <option value="electrical">Electrical</option>
                        <option value="cleaning">Cleaning</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Date Range
                      </label>
                      <select
                        value={filters.dateRange}
                        onChange={(e) => handleFilterChange("dateRange", e.target.value)}
                        className="w-full px-4 py-2 border rounded-lg"
                      >
                        <option value="all">All Time</option>
                        <option value="today">Today</option>
                        <option value="week">This Week</option>
                        <option value="month">This Month</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </Card>

            {/* Orders Table */}
            <Card className="overflow-hidden">
              <div className="overflow-x-auto" style={{ textAlign: "left" }}>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedOrders(orders.map(order => order.id));
                            } else {
                              setSelectedOrders([]);
                            }
                          }}
                        />
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Order Info
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Customer
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {isLoading ? (
                      <tr>
                        <td colSpan={7} className="px-6 py-4 text-center">
                          <div className="flex items-center justify-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            <span className="ml-2">Loading orders...</span>
                          </div>
                        </td>
                      </tr>
                    ) : orders.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                          No orders found
                        </td>
                      </tr>
                    ) : (
                      orders.map((order) => (
                        <tr key={order.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input
                              type="checkbox"
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              checked={selectedOrders.includes(order.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedOrders([...selectedOrders, order.id]);
                                } else {
                                  setSelectedOrders(selectedOrders.filter(id => id !== order.id));
                                }
                              }}
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div>
                                <div className="font-medium text-gray-900">{order.id}</div>
                                <div className="text-sm text-gray-500">{order.service}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{order.customer.name}</div>
                            <div className="text-sm text-gray-500">{order.customer.email}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex flex-col gap-1">
                              <Badge variant={undefined} className={
                                order.status === "pending" ? "bg-yellow-100 text-yellow-700" :
                                  order.status === "in_progress" ? "bg-blue-100 text-blue-700" :
                                    order.status === "completed" ? "bg-green-100 text-green-700" :
                                      order.status === "approved" ? "bg-green-100 text-green-700" :
                                        order.status === "cancelled" ? "bg-red-100 text-red-700" :
                                        order.status === "deleted" ? "bg-red-100 text-red-700" :
                                          order.status === "inactive" ? "bg-gray-100 text-gray-700" :
                                            order.status === "active" ? "bg-green-100 text-green-700" :
                                              "bg-gray-100 text-gray-700"
                              }>
                                {order.status.replace("_", " ").toUpperCase()}
                              </Badge>
                              {order.isFlagged && (
                                <Badge variant="outline" className="border-orange-200 text-orange-700 text-xs">
                                  FLAGGED
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            ${order.amount.toFixed(2)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(order.orderDate).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setShowOrderDetails(order)}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              View Details
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="px-6 py-4 border-t flex items-center justify-between" style={{ textAlign: "left" }}>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-700">
                    Showing page {currentPage} of {totalPages}
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </main>

      {/* Order Details Modal */}
      <OrderDetailsModal
        order={showOrderDetails}
        isOpen={!!showOrderDetails}
        onClose={() => setShowOrderDetails(null)}
      />

      {/* Edit Job Modal */}
      <EditJobModal
        isOpen={editJobModalOpen}
        onClose={() => setEditJobModalOpen(false)}
        job={editingJob}
        onSuccess={handleEditJobSuccess}
      />

      {/* <SharedFooter3 /> */}
    </div>
  );
};

export default AllOrdersPage;